import { MarketPlaceSummary } from "@lib/types/MarketPlace";
import { Drawer, Descriptions, Skeleton, Typography, Divider } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { shortenString } from "@lib/helpers/StringHelper";
import styled from "styled-components";
import dayjs from "dayjs";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  item: MarketPlaceSummary | null;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 7 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const MarketPlaceSummaryDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { messages } = useIntl();

  return (
    <StyledDrawer
      title={
        <FormattedMessage
          id={"common.previewTitle"}
          values={{
            title: messages["sidebar.marketPlace.summaries"] as string,
          }}
        />
      }
      onClose={onClose}
      open={open}
      width={600}
      id="market-place-summary-drawer"
    >
      {!item ? (
        <SkeletonContent />
      ) : (
        <>
          <StyledDescriptions bordered column={1}>
            <Descriptions.Item label="ID">
              <Typography.Text copyable={{ text: item?.id }}>
                {shortenString(item?.id, 10)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.createdAt" />}
            >
              {item?.createdAt
                ? dayjs(item.createdAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.createdBy" />}
            >
              {item?.createdBy || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.updatedAt" />}
            >
              {item?.updatedAt
                ? dayjs(item.updatedAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.updatedBy" />}
            >
              {item?.updatedBy || "-"}
            </Descriptions.Item>
          </StyledDescriptions>

          <Divider />

          <div style={{ marginTop: 16 }}>
            <Typography.Title level={5} style={{ marginBottom: 12 }}>
              <FormattedMessage id="common.summary" />
            </Typography.Title>

            <Typography.Text>{item?.summary || "-"}</Typography.Text>
          </div>

          <Divider />

          <div style={{ marginTop: 16 }}>
            <Typography.Title level={5} style={{ marginBottom: 12 }}>
              <FormattedMessage id="common.opportunities" />
            </Typography.Title>
            {item?.opportunities && item.opportunities.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                {item.opportunities.map((opportunity, index) => (
                  <div key={index} style={{ display: "block" }}>
                    <Typography.Text>{opportunity}</Typography.Text>
                  </div>
                ))}
              </div>
            ) : (
              <Typography.Text>-</Typography.Text>
            )}
          </div>
        </>
      )}
    </StyledDrawer>
  );
};
